import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

const PaymentForm = () => {
  const [formData, setFormData] = useState({
    housingId: '',
    beneficiaryName: '',
    mobile: '',
    district: '',
    block: '',
    panchayat: '',
    scheme: '',
    aadhar: '',
    paymentDate: new Date().toISOString().split('T')[0],
    stageOfPayment: '',
    amount: '',
    releaseMode: ''
  });

  const [housingOptions, setHousingOptions] = useState([]);
  const [houseData, setHouseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ledgerData, setLedgerData] = useState([]);
const paymentStages = [
  'Basement Construction',
  'Lintel Laid',
  'Roof Laid', 
  'Physical Completion'
];

const getAvailablePaymentStages = () => {
  if (!ledgerData || ledgerData.length === 0) {
    return paymentStages;
  }

  // Get stages that have already been paid
  const paidStages = ledgerData.map(payment => payment.stage);

  // Filter out stages that have already been paid
  const availableStages = paymentStages.filter(stage => {
    return !paidStages.includes(stage);
  });

  return availableStages;
};

  // Fetch housing IDs when user types
  const searchHousingIds = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setHousingOptions([]);
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/house/id-search/${searchTerm}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.data && data.data.length > 0) {
        setHousingOptions(data.data);
      } else {
        setHousingOptions([]);
      }
    } catch (err) {
      setHousingOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectDetails = async (houseId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/detail/${houseId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.data) {
        const projectData = data.data;
        
        setFormData(prev => ({
          ...prev,
          district: projectData.district || '',
          block: projectData.block || '',
          panchayat: projectData.panchayat || '',
          scheme: projectData.scheme || '',
          aadhar: projectData.aadhar || ''
        }));
        
        setHouseData(projectData);
        
        // Fetch ledger data after project details
        fetchLedgerData(houseId);
        
        console.log('Project details loaded:', projectData);
      } else {
        console.error('Failed to fetch project details:', data.message || 'No data found');
      }
    } catch (err) {
      console.error('Error fetching project details:', err);
    }
  };

  const fetchLedgerData = async (houseId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/ledger/${houseId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.data) {
        setLedgerData(data.data);
        console.log('Ledger data loaded:', data.data);
      } else {
        setLedgerData([]);
        console.error('Failed to fetch ledger data:', data.message || 'No data found');
      }
    } catch (err) {
      setLedgerData([]);
      console.error('Error fetching ledger data:', err);
    }
  };

  const handleHousingIdChange = (selectedHouse) => {
    setFormData(prev => ({
      ...prev,
      housingId: selectedHouse.h_id,
      beneficiaryName: selectedHouse.beneficiary,
      mobile: selectedHouse.mobile
    }));
    
    setHousingOptions([]);
    fetchProjectDetails(selectedHouse.id);
  };

  const handleSearchChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      housingId: value,
      beneficiaryName: '',
      mobile: ''
    }));
    setHouseData(null);
    setLedgerData([]);
    searchHousingIds(value);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!houseData) {
      setError('Please select a valid Housing ID');
      return;
    }

    if (!formData.paymentDate || !formData.stageOfPayment || !formData.amount || !formData.releaseMode.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        date: formData.paymentDate,
        stage: formData.stageOfPayment,
        amount: Number(formData.amount),
        mode: formData.releaseMode.trim(),
        remarks: 'Payment entry'
      };

      const response = await fetch(`${API_BASE_URL}/ledger/${houseData.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess('Payment submitted successfully!');
        setFormData(prev => ({
          ...prev,
          paymentDate: new Date().toISOString().split('T')[0],
          stageOfPayment: '',
          amount: '',
          releaseMode: ''
        }));
        
        // Refresh ledger data
        fetchLedgerData(houseData.id);
      } else {
        setError(data.message || 'Failed to submit payment');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="space-y-6">
          
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Form</h1>
            <p className="text-gray-600">Record payment details for housing projects</p>
          </div>

          {/* Success/Error Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}

          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Housing ID *</label>
              <div className="relative" style={{ width: '45%' }}>
                <input
                  type="text"
                  value={formData.housingId}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Type to search Housing ID..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {loading && (
                  <p className="text-xs text-blue-600 mt-1">Searching...</p>
                )}
                {housingOptions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {housingOptions.map(house => (
                      <div
                        key={house.id}
                        onClick={() => handleHousingIdChange(house)}
                        className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium">{house.h_id}</div>
                        <div className="text-gray-600 text-xs">{house.beneficiary} - {house.mobile}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Property Information Grid */}
            {houseData && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <h3 className="text-md font-semibold text-gray-900 mb-4 pb-2 border-b border-blue-200">Property Information</h3>
                
                {/* First Row: Housing ID, Panchayat, Block, Scheme, Year */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1">Housing ID</label>
                    <div className="text-sm text-gray-900 font-medium">{formData.housingId}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1">Panchayat</label>
                    <div className="text-sm text-gray-900 font-medium">{formData.panchayat}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1">Block</label>
                    <div className="text-sm text-gray-900 font-medium">{formData.block}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1">Scheme</label>
                    <div className="text-sm text-gray-900 font-medium">{formData.scheme}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1">Year</label>
                    <div className="text-sm text-gray-900 font-medium">{houseData.s_year || 'N/A'}</div>
                  </div>
                </div>

                {/* Second Row: Name, Mobile, Aadhar */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1">Beneficiary Name</label>
                    <div className="text-sm text-gray-900 font-medium">{formData.beneficiaryName}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1">Mobile Number</label>
                    <div className="text-sm text-gray-900 font-medium">{formData.mobile}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1">Aadhar</label>
                    <div className="text-sm text-gray-900 font-medium">{formData.aadhar}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1"></label>
                    <div className="text-sm text-gray-900 font-medium"></div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1"></label>
                    <div className="text-sm text-gray-900 font-medium"></div>
                  </div>
                </div>
              </div>
            )}

            {/* All Payments List from Ledger */}
            {ledgerData && ledgerData.length > 0 && (
              <div className="mb-6">
                <h3 className="text-md font-semibold text-gray-900 mb-4 pb-2 border-b border-blue-200">All Payments</h3>
                <div className="space-y-4">
                  {ledgerData.map((payment, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div>
                          <label className="text-sm font-medium text-gray-600 block mb-1">Stage</label>
                          <div className="text-sm text-gray-900 font-medium">{payment.stage}</div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 block mb-1">Date</label>
                          <div className="text-sm text-gray-900 font-medium">{new Date(payment.date).toLocaleDateString()}</div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 block mb-1">Amount Released</label>
                          <div className="text-sm text-gray-900 font-medium">₹{parseInt(payment.amount).toLocaleString()}</div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 block mb-1">Mode of Release</label>
                          <div className="text-sm text-gray-900 font-medium">{payment.mode || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Payment Form */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Date of Payment *
                </label>
                <input
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => handleInputChange('paymentDate', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Stage of Payment *
                </label>
       <select
  value={formData.stageOfPayment}
  onChange={(e) => handleInputChange('stageOfPayment', e.target.value)}
  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
>
  <option value="">Select payment stage</option>
  {getAvailablePaymentStages().map(stage => (
    <option key={stage} value={stage}>{stage}</option>
  ))}
</select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Amount Released *
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Mode of Release *
                </label>
                <input
                  type="text"
                  value={formData.releaseMode}
                  onChange={(e) => handleInputChange('releaseMode', e.target.value)}
                  placeholder="Enter release mode"
                  maxLength={100}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Submit Actions */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !houseData || !formData.paymentDate || !formData.stageOfPayment || !formData.amount || !formData.releaseMode.trim()}
                className={`inline-flex items-center px-6 py-2 text-sm rounded-md font-medium ${
                  houseData && !submitting && formData.paymentDate && formData.stageOfPayment && formData.amount && formData.releaseMode.trim()
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {submitting ? 'Submitting...' : 'Submit Payment'}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PaymentForm;